import { useSelector } from 'react-redux';
import { DeleteIcon } from '../../assets/icons/DeleteIcon';
import { selectUserProfile } from '../../stateManagement/reducers/userProfileReducer';
import AssignCaseWorkerMenu from '../../views/task-view/task-list-view/task-list-tools/AssignCaseWorkerMenu';
import ChangeDepartmentMenu from '../../views/task-view/task-list-view/task-list-tools/ChangeDepartmentMenu';
import ChangeStatusMenu from '../../views/task-view/task-list-view/task-list-tools/ChangeStatusMenu';
import PlanningSystemMenu from '../../views/task-view/task-list-view/task-list-tools/PlanningSystemMenu';
import TableMenuBar, { StyledButton } from '../table/table-menu-bar/TableMenuBar';
import { useMemo } from 'react';
import { isInPlanningLoop } from '../../views/task-view/task-list-view/TaskListFilterUtils';

interface Props {
  selectedWorkTasks: { workTaskId: string; hasSubTasks: boolean; status?: string; department?: string }[];
  updateTasks: () => void;
  handleClose: () => void;
  showBookButton?: boolean;
  handleDeleteTasks?: (worktaskHasDifferentDepartment: boolean) => void;
  setLoading: (loading: boolean) => void;
}

const WorkTaskControlBar = (props: Props) => {
  const { selectedWorkTasks, updateTasks, handleClose, showBookButton, handleDeleteTasks, setLoading } = props;

  const userProfile = useSelector(selectUserProfile).userProfile;
  const userDepartment = userProfile?.department;

  const worktaskHasDifferentDepartment = selectedWorkTasks.some(
    (w) => w.department !== '-' && w.department !== userDepartment?.name
  );

  const showChangeStatusButton = useMemo(() => {
    return !isInPlanningLoop(selectedWorkTasks.map((wt) => wt.status as string));
  }, [selectedWorkTasks]);

  return (
    <TableMenuBar
      selectedItems={selectedWorkTasks.map((x) => ({ id: x.workTaskId }))}
      rowNameSingle={'opgave'}
      rowNameMultiple={'opgaver'}
      onClose={handleClose}
    >
      {showChangeStatusButton && (
        <ChangeStatusMenu
          fetchTasksCallback={updateTasks}
          workTaskIds={selectedWorkTasks.map((x) => ({
            id: +x.workTaskId,
            hasSubTasks: x.hasSubTasks ?? false,
            status: x.status
          }))}
          setIsLoading={(isLoading: boolean) => setLoading(isLoading)}
          worktaskHasDifferentDepartment={worktaskHasDifferentDepartment}
        />
      )}
      <ChangeDepartmentMenu
        fetchTasksCallback={updateTasks}
        selectedRows={selectedWorkTasks.map((x) => ({ id: x.workTaskId, hasSubTasks: x.hasSubTasks ?? false }))}
        userDepartment={userDepartment}
        setIsLoading={(isLoading: boolean) => setLoading(isLoading)}
        worktaskHasDifferentDepartment={worktaskHasDifferentDepartment}
      />
      <AssignCaseWorkerMenu
        selectedRows={selectedWorkTasks.map((x) => ({ id: x.workTaskId, hasSubTasks: x.hasSubTasks ?? false }))}
        fetchTasksCallback={updateTasks}
        setIsLoading={(isLoading: boolean) => setLoading(isLoading)}
        worktaskHasDifferentDepartment={worktaskHasDifferentDepartment}
      />
      {showBookButton && (
        <PlanningSystemMenu
          selectedWorkTasks={selectedWorkTasks}
          updateTasks={updateTasks}
          setIsLoading={(isLoading: boolean) => setLoading(isLoading)}
          worktaskHasDifferentDepartment={worktaskHasDifferentDepartment}
        />
      )}
      {handleDeleteTasks && (
        <StyledButton
          onClick={() => handleDeleteTasks(worktaskHasDifferentDepartment)}
          variant="tertiary"
          square
          data-testid="delete-task"
        >
          <DeleteIcon size="22px" />
          Annuller opgave
        </StyledButton>
      )}
    </TableMenuBar>
  );
};

export default WorkTaskControlBar;
